import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import {
  ShieldCheck, FileText, LockKeyhole, MapPin, Search, Users, ChevronRight,
  Building2, HardHat, Briefcase, Laptop,
  Truck, Home as HomeIcon, Package, MoreHorizontal, ArrowRight, Loader2, SearchX, X,
} from "lucide-react";
import { Tabs, Section } from "../components/ui/kit";
import { HomeHero } from "../components/HomeHero";
import { PublicProfileCard, ServiceCard, CompanyCard, JobCard, ProjectCard, ItemCard } from "../components/cards";
import { categories, services, companies, jobs, projects, stats } from "../data/mock";
import { api } from "../lib/api";
import { MOCK_DATA_ENABLED } from "../lib/dataMode";
import { useI18n } from "../lib/i18n";
import { isNativeApp } from "../lib/platform";
import { useAuth } from "../lib/auth";
import { profileKey } from "../lib/profileSession";
import { useSavedMutationPending } from "../lib/saved";
import { refreshFamily, useRefreshOwner } from "../lib/useRefreshOwner";
import { PullToRefresh } from "../components/PullToRefresh";
import { postHref } from "../lib/postNavigation";

// Services carry the price under `fromPrice` from the API; cards read `from`.
const mapService = (s: any) => ({ ...s, from: s.fromPrice ?? s.from ?? 0 });
const featuredFallbacks = { people: [], services, companies, jobs, projects };

const catIcons: Record<string, any> = { HardHat, Briefcase, Laptop, Building2, Truck, Home: HomeIcon, Package, MoreHorizontal };

const heroTrustItems = [
  { key: "verified", icon: ShieldCheck, title: "Verified Profiles", description: "Real people. Real skills." },
  { key: "payments", icon: LockKeyhole, title: "Secure Payments", description: "Safe and hassle-free transactions." },
  { key: "contracts", icon: FileText, title: "Clear Contracts", description: "Set expectations. Work with confidence." },
] as const;

const approvedCategoryCards = [
  { label: "Construction", image: "/hero/construction.jpg", href: "/marketplace?tab=Services&category=Construction" },
  { label: "Electrical", image: "/hero/electrical.jpg", href: "/marketplace?tab=Services&category=Electrical" },
  { label: "Plumbing", image: "/hero/plumbing.jpg", href: "/marketplace?tab=Services&category=Plumbing" },
  { label: "Home Services", image: "/hero/home-service.jpg", href: "/marketplace?tab=Services&category=Home" },
  { label: "Professional Services", image: "/hero/professional-services.jpg", href: "/marketplace?tab=Services&category=Professional" },
] as const;

// Friendly category label → concise keyword the marketplace filters on
// (aligns with the marketplace's own category options where possible).
const CATEGORY_KEYWORD: Record<string, string> = {
  "Construction & Trade": "Construction",
  "Professional Services": "Professional",
  "IT & Digital Services": "IT",
  "Business Services": "Business",
  "Logistics & Transport": "Logistics",
  "Home & Personal": "Home",
  "Supplies & Equipment": "Supplies",
  "Others": "Others",
};

const FEATURED_TAB_LABEL_KEYS: Record<string, string> = {
  Recommended: "home.recommended",
  Services: "discovery.services",
  Jobseekers: "discovery.jobseekers",
  Companies: "discovery.companies",
  Jobs: "discovery.jobs",
  "Project Bidding": "discovery.projects",
};

export default function Home() {
  const { lang, t } = useI18n();
  const featuredTabLabel = (tab: string) => FEATURED_TAB_LABEL_KEYS[tab] ? t(FEATURED_TAB_LABEL_KEYS[tab]) : tab;
  const translatedSectionHeaderClassName = lang === "tl" ? "flex-col items-start sm:flex-row sm:items-end" : "";
  const [featTab, setFeatTab] = useState("Recommended");
  const featuredGridClassName = `grid grid-cols-2 items-stretch gap-3 sm:gap-4 ${isNativeApp() ? "lg:grid-cols-3" : "lg:grid-cols-4"}`;

  // Featured rows prefer live marketplace posts and use configured prototype
  // records only when the build explicitly enables them.
  const emptyFeatured = { marketplace: [], people: [], services: [], companies: [], jobs: [], projects: [] };
  const [feat, setFeat] = useState<{ marketplace: any[]; people: any[]; services: any[]; companies: any[]; jobs: any[]; projects: any[] }>(
    MOCK_DATA_ENABLED ? { marketplace: [], ...featuredFallbacks } : emptyFeatured,
  );
  const hasFeaturedItems = featTab === "Marketplace" ? feat.marketplace.length > 0
    : featTab === "Jobseekers" ? feat.people.length > 0
    : featTab === "Services" ? feat.services.length > 0
      : featTab === "Companies" ? feat.companies.length > 0
        : featTab === "Jobs" ? feat.jobs.length > 0
          : featTab === "Project Bidding" ? feat.projects.length > 0
            : Object.values(feat).some((items) => items.length > 0);

  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  // [#29] Live per-category counts so the landing cards match the marketplace.
  const [catCounts, setCatCounts] = useState<Record<string, number>>({});
  const [banners, setBanners] = useState<Awaited<ReturnType<typeof api.banners>>>([]);
  const { user } = useAuth();
  const savedPending = useSavedMutationPending();
  const identity = `${profileKey(user)}:home`;
  const refresh = useRefreshOwner(identity, signal => [
    refreshFamily(() => api.listItems(undefined, signal), marketplace => setFeat(current => ({ ...current, marketplace })), () => setFeat(current => ({ ...current, marketplace: [] }))),
    ...(["people", "services", "companies", "jobs", "projects"] as const).map(kind => refreshFamily(
      () => api.list(kind, undefined, signal), rows => {
        const featured = rows.length || !MOCK_DATA_ENABLED ? rows : featuredFallbacks[kind];
        setFeat(current => ({ ...current, [kind]: kind === "services" ? featured.map(mapService) : featured }));
      },
      () => setFeat(current => ({ ...current, [kind]: [] })))),
    refreshFamily(async () => {
      const response = await api.categoryCounts(signal);
      if (!response?.counts || typeof response.counts !== "object") throw new Error("Missing category counts.");
      return response.counts;
    }, setCatCounts, () => setCatCounts({})),
    refreshFamily(() => api.banners("promo", signal), setBanners, () => setBanners([])),
  ]);
  const featLoading = !refresh.loaded;
  const [, navigate] = useLocation();
  // [#60] Clicking a Popular Category opens that category in the Market (was:
  // only scrolled the page). Maps the friendly label to the marketplace keyword.
  // [#60/#29] Opens the category in the Services tab — the same tab the landing
  // count is computed from — so the card number and the marketplace result agree.
  function pickCategory(label: string) {
    const params = new URLSearchParams();
    params.set("tab", "Services");
    const catKeyword = CATEGORY_KEYWORD[label] ?? label;
    if (catKeyword.trim()) params.set("category", catKeyword.trim());
    navigate(`/marketplace?${params.toString()}`);
  }

  function runSearch(e?: React.FormEvent) {
    e?.preventDefault();
    const params = new URLSearchParams();
    params.set("tab", "Services");
    if (q.trim()) params.set("q", q.trim());
    if (city.trim()) params.set("city", city.trim());
    // Map the friendly label to the keyword the marketplace filters on.
    const catKeyword = category ? (CATEGORY_KEYWORD[category] ?? category) : "";
    if (catKeyword.trim()) params.set("category", catKeyword.trim());
    navigate(`/marketplace?${params.toString()}`);
  }

  return (
    <PullToRefresh identity={`${identity}:${featTab}:${savedPending}`} disabled={savedPending || (refresh.reading && !refresh.loaded)} onRefresh={refresh.onRefresh}>
    <div>
      {refresh.error && <p role="alert" className="container-x py-3 text-sm text-brand-red">{refresh.error}</p>}
      <HomeHero>
        <div className="hero-action-links grid grid-cols-2 gap-3">
          <Link href="/marketplace?tab=Jobseekers" className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-[#ff641d] px-3 text-sm sm:text-base font-bold text-white hover:bg-[#e65010]"><Users className="h-5 w-5" />{t("home.findJobseekers")}</Link>
          <Link href="/marketplace?tab=Jobs" className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-lg border border-navy bg-navy px-3 text-sm sm:text-base font-bold text-white hover:bg-navy-600"><Briefcase className="h-5 w-5" />{t("home.findWork")}</Link>
        </div>
        <form className="hero-search mt-4 w-full xl:mt-0 rounded-xl border border-white/80 bg-white/90 p-2 shadow-lift" onSubmit={runSearch}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <label className="relative min-w-0 flex-1">
              <span className="sr-only">Service or skill</span>
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-mut" />
              <input id="home-search-query" className="field border-0 bg-transparent pl-9 shadow-none focus:ring-0" value={q} onChange={(event) => setQ(event.target.value)} placeholder={t("home.search.servicePlaceholder")} />
            </label>
            <label className="relative min-w-0 flex-1 border-line sm:border-l">
              <span className="sr-only">City or province</span>
              <MapPin className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-mut" />
              <input className="field border-0 bg-transparent pl-9 shadow-none focus:ring-0" value={city} onChange={(event) => setCity(event.target.value)} placeholder={t("home.search.city")} />
            </label>
            <button type="submit" className="h-11 rounded-lg bg-[#ff641d] px-7 text-sm font-semibold text-white transition-colors hover:bg-[#e65010]">{t("home.search.button")}</button>
          </div>
          {category && (
            <div className="flex items-center gap-2 px-2 pb-1 pt-2 text-sm">
              <span className="text-mut">Category:</span>
              <span className="chip bg-navy text-white">{category}<button type="button" onClick={() => setCategory("")} className="-mr-1 ml-1 hover:opacity-80" aria-label="Clear category"><X className="h-3 w-3" /></button></span>
            </div>
          )}
        </form>
      </HomeHero>

      {/* TRUST STRIP */}
      <section className="border-b border-line bg-white">
        <div className="container-x grid grid-cols-3 divide-x divide-line py-4">
          {heroTrustItems.map(({ key, icon: Icon, title, description }) => (
            <div key={key} className="flex flex-col items-center gap-1 px-2 py-2 text-center sm:flex-row sm:justify-start sm:gap-3 sm:px-4 sm:py-3 sm:text-left">
              <Icon className="h-7 w-7 shrink-0 text-navy sm:h-9 sm:w-9" strokeWidth={1.7} />
              <div><p className="text-[11px] font-extrabold leading-tight text-navy sm:text-base">{t(`home.trust.${key}.title`, title)}</p><p className="hidden text-sm text-mut sm:block">{t(`home.trust.${key}.description`, description)}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* PROMO BANNERS (managed in Admin → Banners) */}
      {banners.length > 0 && (
        <section className="container-x pt-8">
          <div className={`grid gap-4 ${banners.length === 1 ? "" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
            {banners.map((b) => {
              const inner = (
                <div className="relative overflow-hidden rounded-xl bg-navy text-white h-full min-h-[140px] flex flex-col justify-center p-5 hover:shadow-lift transition-shadow">
                  {b.imageUrl && <img src={api.apiAssetUrl(b.imageUrl) || undefined} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
                  <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" aria-hidden="true" />
                  <div className="relative z-10">
                    <h3 className="font-extrabold text-lg leading-snug">{b.title}</h3>
                    {b.subtitle && <p className="text-sm text-white/75 mt-1">{b.subtitle}</p>}
                    {b.ctaLabel && <span className="inline-flex items-center gap-1 mt-3 text-brand-gold font-semibold text-sm">{b.ctaLabel} <ArrowRight className="w-4 h-4" /></span>}
                  </div>
                </div>
              );
              return b.ctaHref
                ? (b.ctaHref.startsWith("http")
                    ? <a key={b.id} href={b.ctaHref} target="_blank" rel="noreferrer" className="block">{inner}</a>
                    : <Link key={b.id} href={b.ctaHref} className="block">{inner}</Link>)
                : <div key={b.id}>{inner}</div>;
            })}
          </div>
        </section>
      )}

      {/* APPROVED SERVICE CATEGORIES */}
      <section className="bg-[#fbfaf8] py-8 sm:py-9">
        <div className="mx-auto w-[91%] max-w-[1390px]">
          <h2 className="text-base font-extrabold tracking-tight text-navy sm:text-4xl">{t("home.peopleHeading")}</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
            {approvedCategoryCards.map((categoryCard) => (
              <Link key={categoryCard.label} href={categoryCard.href} className="overflow-hidden rounded-xl border border-line bg-white shadow-card transition hover:shadow-lift">
                <img src={categoryCard.image} alt="" className="h-28 w-full object-cover sm:h-44" />
                <div className="flex items-center justify-between px-3 py-2 text-sm font-extrabold text-navy sm:px-4 sm:py-3 sm:text-lg"><span>{categoryCard.label}</span><ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular category labels are display copy; clicks keep the existing category query values. */}
      <Section
        title={t("home.popularCategories")}
        action={<Link href="/marketplace" className="text-sm font-semibold text-brand-red flex items-center gap-1">{t("home.viewAllCategories")} <ChevronRight className="w-4 h-4" /></Link>}
        headerClassName={translatedSectionHeaderClassName}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {categories.map((c) => {
            const I = catIcons[c.icon];
            const selected = category === c.label;
            return (
              <button type="button" key={c.key} onClick={() => pickCategory(c.label)}
                aria-pressed={selected}
                className={`card p-4 flex flex-col items-center text-center gap-2 transition hover:shadow-lift ${selected ? "border-navy-500 ring-2 ring-navy/30 bg-navy-50/50" : "hover:border-navy-500"}`}>
                <div className={`w-11 h-11 rounded-lg grid place-items-center ${selected ? "bg-navy text-white" : "bg-navy-50 text-navy"}`}><I className="w-5 h-5" /></div>
                <span className="text-xs font-semibold text-navy leading-tight">{t(`home.category.${c.key}`, c.label)}</span>
                {(() => { const kw = CATEGORY_KEYWORD[c.label]; const n = kw ? catCounts[kw] : undefined; return typeof n === "number" ? <span className="text-[11px] text-mut">{n.toLocaleString()} {t(n === 1 ? "home.category.serviceCount" : "home.category.servicesCount")}</span> : null; })()}
              </button>
            );
          })}
        </div>
      </Section>

      {/* FEATURED MARKETPLACE */}
      <Section title={t("home.featured")} action={<Link href="/marketplace" className="text-sm font-semibold text-brand-red flex items-center gap-1">{t("home.viewAll")} <ArrowRight className="w-4 h-4" /></Link>} headerClassName={translatedSectionHeaderClassName}>
        <div className="mb-5"><Tabs tabs={["Recommended", "Services", "Marketplace", "Jobseekers", "Companies", "Jobs", "Project Bidding"]} active={featTab} onChange={setFeatTab} labelForTab={featuredTabLabel} /></div>
        {featLoading ? (
          <div className="card p-10 text-center text-mut">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-navy" />
            {t("home.featuredLoading")}
          </div>
        ) : !hasFeaturedItems ? (
          <div className="card p-10 text-center">
            <SearchX className="w-8 h-8 text-mut mx-auto mb-2" />
            <div className="font-bold text-navy">{t("home.featuredEmpty")}</div>
            <p className="text-sm text-mut mt-1">{t("home.featuredEmptyHint")}</p>
          </div>
        ) : featTab === "Recommended" ? (
          // Recommended mixes image-led listings with compact profiles. Keep them in
          // separate groups, then stretch each group's cards to its tallest sibling
          // so every row has a clean, consistent edge.
          (() => {
            const withPhotos = [
              feat.services[0] && <ServiceCard key="r-s0" s={feat.services[0]} />,
              feat.jobs[0] && <JobCard key="r-j0" j={feat.jobs[0]} />,
              feat.projects[0] && <ProjectCard key="r-pr0" p={feat.projects[0]} />,
              feat.services[1] && <ServiceCard key="r-s1" s={feat.services[1]} />,
              feat.projects[1] && <ProjectCard key="r-pr1" p={feat.projects[1]} />,
              feat.jobs[1] && <JobCard key="r-j1" j={feat.jobs[1]} />,
            ].filter(Boolean);
            const profiles = [
              feat.people[0] && <PublicProfileCard key="r-p0" profile={feat.people[0]} />,
              feat.companies[0] && <CompanyCard key="r-c0" c={feat.companies[0]} webGrid />,
              feat.people[1] && <PublicProfileCard key="r-p1" profile={feat.people[1]} />,
              feat.companies[1] && <CompanyCard key="r-c1" c={feat.companies[1]} webGrid />,
              feat.people[2] && <PublicProfileCard key="r-p2" profile={feat.people[2]} />,
              feat.people[3] && <PublicProfileCard key="r-p3" profile={feat.people[3]} />,
            ].filter(Boolean);
            return (
              <div className="space-y-6">
                {withPhotos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-navy mb-3">{t("home.featuredListings")}</h3>
                    <div className={featuredGridClassName}>{withPhotos}</div>
                  </div>
                )}
                {profiles.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-navy mb-3">{t("home.featuredProfiles")}</h3>
                    <div className={featuredGridClassName}>{profiles}</div>
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <div className={featuredGridClassName}>
            {(featTab === "Marketplace") && feat.marketplace.slice(0, 6).map((item) => <ItemCard key={item.id} it={item} />)}
            {(featTab === "Jobseekers") && feat.people.slice(0, 6).map((p) => <PublicProfileCard key={p.id} profile={p} />)}
            {(featTab === "Services") && feat.services.slice(0, 6).map((s) => <ServiceCard key={s.id} s={s} />)}
            {(featTab === "Companies") && feat.companies.slice(0, 6).map((c) => <CompanyCard key={c.id} c={c} webGrid />)}
            {(featTab === "Jobs") && feat.jobs.slice(0, 6).map((j) => <JobCard key={j.id} j={j} />)}
            {(featTab === "Project Bidding") && feat.projects.slice(0, 6).map((p) => <ProjectCard key={p.id} p={p} />)}
          </div>
        )}
      </Section>

      {/* STATS BAND */}
      {stats.length > 0 && (
        <section className="bg-navy-50 border-y border-line">
          <div className="container-x py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {stats.map((s) => (
              <div key={s.lbl}><div className="text-3xl font-extrabold text-navy">{s.big}</div><div className="text-sm text-mut mt-1">{s.lbl}</div></div>
            ))}
          </div>
          <p className="text-center text-[11px] text-mut pb-4">Prototype figures — replace with verified platform metrics before launch.</p>
        </section>
      )}

      {/* BUSINESS + FINAL CTA */}
      <section className="container-x py-12 grid lg:grid-cols-2 gap-4">
        <div className="card p-8 bg-navy text-white flex flex-col justify-center">
          <h3 className="text-2xl font-extrabold">{t("home.workforce.title")}</h3>
          <p className="text-white/70 mt-2">{t("home.workforce.description")}</p>
          <div className="mt-4"><Link href="/signup" className="btn-gold">{t("home.workforce.action")}</Link></div>
        </div>
        <div className="card p-8 flex flex-col justify-center">
          <h3 className="text-2xl font-extrabold text-navy">{t("home.finalCta.title")}</h3>
          <p className="text-mut mt-2">{t("home.finalCta.description")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link href={postHref("job", "/")} className="btn-red">{t("home.finalCta.postJob")}</Link>
            <Link href="/worker-profile" className="btn-outline">{t("home.finalCta.createProfile")}</Link>
          </div>
        </div>
      </section>
    </div>
    </PullToRefresh>
  );
}
